import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, TextField, Button, Paper, IconButton, Tooltip, MenuItem, Menu, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Drawer, Dialog, DialogTitle, DialogContent, DialogActions,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import { BRAND } from "../../styles/theme";
import type { CollectorCtx } from "../../pages/CollectorDetail";
import {
  Account, UploadTask, listAccounts, listAllAccounts, addAccount, editAccountCustomerAndEmail, editAccountAlias,
  setAccountEnabled, listUploadTasks, uploadAccountsFile, downloadUploadFile, getAccount, setAccountDescription,
} from "../../services/collectors";
import { buscarCuenta } from "../../services/monitoreoVivo";
import { getBatchStatus, startBatch, type BatchStatus } from "../../services/accountBatch";
import { StatusChip, fmtDateTime } from "./chips";
import ConfirmDialog from "./ConfirmDialog";
import { isDescriptionHeader, normalizeDescription, descriptionError, isTaskFinished, pickUploadTask } from "../../lib/accountDescriptions";

type StatusFilter = "active" | "disabled" | "all";
const squareInput = { "& .MuiOutlinedInput-root": { borderRadius: 1 } };
const ALIAS_RE = /^[A-Za-z0-9.-]{6,20}$/;
// Estado "terminó bien" de una tarea de Upload history (mismo criterio que el chip).
const TASK_OK_RE = /DONE|OK|SUCCESS|COMPLET/i;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

export default function AccountsTab({ ctx }: { ctx: CollectorCtx }) {
  const { collector, config, notify } = ctx;
  const [sp, setSp] = useSearchParams();
  const q      = sp.get("q") ?? "";
  const status = (sp.get("status") as StatusFilter) || "active";
  const setParam = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };

  const [rows, setRows]         = useState<Account[]>([]);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const [more, setMore]         = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [addOpen, setAddOpen]   = useState(false);
  const [edit, setEdit]         = useState<Account | null>(null);
  const [toggle, setToggle]     = useState<Account | null>(null);
  const [histOpen, setHistOpen] = useState(false);
  const [upOpen, setUpOpen]     = useState(false);
  const [menuEl, setMenuEl]     = useState<HTMLElement | null>(null);
  const [bulk, setBulk]         = useState<"disable" | "enable" | null>(null);
  const [tasks, setTasks]       = useState<UploadTask[] | null>(null);
  const [batch, setBatch]       = useState<BatchStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const batchRunning = !!batch?.running;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await listAccounts(collector.id);
      setRows(r.items); setNextPage(r.nextPage);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }, [collector.id]);
  useEffect(() => { void load(); }, [load]);

  async function loadMore() {
    if (!nextPage) return;
    setMore(true);
    try {
      const r = await listAccounts(collector.id, nextPage);
      setRows((prev) => [...prev, ...r.items]); setNextPage(r.nextPage);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setMore(false); }
  }

  // CP-425: el export anterior solo serializaba `rows` (lo ya cargado en pantalla,
  // una sola página de listAccounts) — con collectors de miles de cuentas eso
  // truncaba el CSV a la primera página. Acá se agota el cursor de paginación
  // desde cero, sin depender de lo que esté cargado en la tabla.
  async function exportAllAccounts() {
    setExporting(true); setErr(null);
    notify("Preparing CSV export — fetching all pages, this can take a moment for large collectors…");
    try {
      exportCsv(collector.id, await listAllAccounts(collector.id));
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setExporting(false); }
  }

  const loadTasks = useCallback(async () => {
    try { setTasks(await listUploadTasks(collector.id)); }
    catch (e: any) { notify(String(e?.message ?? e), "error"); }
  }, [collector.id, notify]);
  useEffect(() => { if (histOpen) void loadTasks(); }, [histOpen, loadTasks]);
  useEffect(() => { void loadTasks(); }, [loadTasks]);
  const running = (tasks ?? []).filter((t) => /PROCESS|PENDING|QUEUED|RUNNING/i.test(t.status)).length;
  // CP-444: el badge "N running" del botón solo se cargaba al montar o al abrir el
  // historial — si quedaba un upload en proceso, se veía "trabado" hasta que el
  // usuario reabría el drawer. Pollear mientras haya tareas corriendo, igual que
  // ya se hace para el batch masivo más abajo.
  useEffect(() => {
    if (running === 0) return;
    taskPollRef.current = setTimeout(() => { void loadTasks(); }, 3000);
    return () => { if (taskPollRef.current) clearTimeout(taskPollRef.current); };
  }, [running, loadTasks]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((a) =>
      (status === "all" || (status === "active") === !a.deleted) &&
      (!s || [a.accountNumber, a.customerId, a.alias, a.email ?? "", a.description].some((v) => v.toLowerCase().includes(s))));
  }, [rows, q, status]);

  // CP-458: `rows` solo tiene la página ya cargada (listAccounts pagina de a 80), así que
  // buscar por alias (o CVU de otra página) no encontraba nada salvo que ya estuviera
  // cargada. El buscador de alias/CVU de Monitoreo (misma GSI, ya desplegado, cero costo
  // nuevo) resuelve a qué collector pertenece — si matchea este collector y no está ya en
  // `rows`, se trae la cuenta puntual (getAccount) y se mezcla. customer_id queda afuera:
  // ese buscador no lo soporta (no hay índice por customer_id, ver CP-458).
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const query = q.trim();
    // Solo si el filtro local no encontró nada: evita pegarle al buscador remoto en
    // cada tecla cuando la cuenta ya está en la página cargada (costo real, aunque chico).
    if (!query || query.length < 3 || filtered.length > 0) return;
    let alive = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { items } = await buscarCuenta(query);
        const hits = items.filter((it) => (it.collector ?? "").toLowerCase() === collector.id.toLowerCase());
        const missing = hits.filter((h) => !rows.some((r) => r.accountNumber === h.cvu));
        if (!missing.length) return;
        const fetched = (await Promise.all(missing.map((h) => getAccount(collector.id, h.cvu)))).filter((a): a is Account => !!a);
        if (alive && fetched.length) {
          setRows((prev) => [...fetched.filter((a) => !prev.some((r) => r.accountNumber === a.accountNumber)), ...prev]);
        }
      } catch { /* el buscador es un complemento best-effort del filtro local */ }
      finally { if (alive) setSearching(false); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, collector.id]);

  const canCreate = (config.create_accounts ?? "").toString().toLowerCase() !== "false";

  async function doToggle() {
    if (!toggle) return;
    await setAccountEnabled(toggle, !!toggle.deleted);   // si falla, el ConfirmDialog muestra el error
    notify(toggle.deleted ? "Account enabled" : "Account disabled");
    setToggle(null); await load();
  }

  // ----- Batch masivo (server-side, desacoplado del browser) ------------------
  const prevRunning = useRef(false);
  const refreshBatch = useCallback(async () => {
    try {
      const s = await getBatchStatus(collector.id);
      setBatch(s);
      if (prevRunning.current && !s.running) {   // terminó → avisar y recargar
        const verb = s.action === "disable" ? "disabled" : "enabled";
        notify(`Batch finished — ${s.ok} ${verb}${s.failed ? `, ${s.failed} failed` : ""}. Details emailed to operaciones@.`, s.failed ? "error" : "success");
        await load();
      }
      prevRunning.current = s.running;
      return s;
    } catch { return null; }
  }, [collector.id, notify, load]);
  useEffect(() => { void refreshBatch(); }, [refreshBatch]);
  useEffect(() => {
    if (!batchRunning) return;
    pollRef.current = setTimeout(() => { void refreshBatch(); }, 3000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [batchRunning, batch, refreshBatch]);

  async function doBulk() {
    if (!bulk) return;
    const action = bulk;
    const { status } = await startBatch(collector.id, action);   // si falla (ej. 409), el ConfirmDialog lo muestra
    setBulk(null);
    setBatch(status);
    prevRunning.current = true;
    notify(`Batch ${action} started — operaciones@ will get an email when it finishes.`);
  }

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Account number, customer id, alias, email, description…" value={q}
          onChange={(e) => setParam("q", e.target.value)} sx={{ width: 320 }}
          InputProps={{ endAdornment: searching ? <CircularProgress size={14} /> : undefined }}
          helperText={searching ? "Checking alias/CVU matches not loaded on this page yet…" : " "} />
        <TextField size="small" select label="Status" value={status} onChange={(e) => setParam("status", e.target.value === "active" ? "" : e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="active">Active</MenuItem><MenuItem value="disabled">Disabled</MenuItem><MenuItem value="all">All</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => setHistOpen(true)} sx={{ borderColor: BRAND.primary, color: BRAND.primary, height: 40 }}>
          Upload history{running > 0 && <Box component="span" sx={{ ml: 1 }}><StatusChip kind="info" label={`${running} running`} /></Box>}
        </Button>
        <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUpOpen(true)} sx={{ borderColor: BRAND.primary, color: BRAND.primary, height: 40 }}>Upload file</Button>
        <Tooltip title={canCreate ? "" : "create_accounts is false for this collector"}>
          <span><Button variant="contained" startIcon={<AddIcon />} disabled={!canCreate} onClick={() => setAddOpen(true)} sx={{ height: 40 }}>Add account</Button></span>
        </Tooltip>
        <Tooltip title="More actions"><IconButton onClick={(e) => setMenuEl(e.currentTarget)} sx={{ border: 1, borderColor: "divider" }}><MoreVertIcon /></IconButton></Tooltip>
        <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
          <MenuItem disabled={batchRunning} onClick={() => { setMenuEl(null); setBulk("disable"); }} sx={{ color: "error.main" }}>
            <PowerSettingsNewIcon fontSize="small" sx={{ mr: 1 }} />
            <Box><Typography variant="body2">Disable all accounts</Typography>
              <Typography variant="caption" color="text.secondary">Rejects incoming transfers on every active account of this collector</Typography></Box>
          </MenuItem>
          <MenuItem disabled={batchRunning || (batch?.disabled_count ?? 0) === 0} onClick={() => { setMenuEl(null); setBulk("enable"); }}>
            <CheckIcon fontSize="small" sx={{ mr: 1 }} />
            <Box><Typography variant="body2">Enable all accounts</Typography>
              <Typography variant="caption" color="text.secondary">Re-enables the {batch?.disabled_count ?? 0} account(s) this admin disabled</Typography></Box>
          </MenuItem>
          <MenuItem disabled={exporting} onClick={() => { setMenuEl(null); void exportAllAccounts(); }}>
            {exporting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <DownloadIcon fontSize="small" sx={{ mr: 1 }} />}
            <Typography variant="body2">{exporting ? "Exporting all accounts…" : "Export all accounts (CSV)"}</Typography>
          </MenuItem>
        </Menu>
      </Stack>

      {!canCreate && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Account creation is disabled for this collector (<code>create_accounts = false</code>).{" "}
          <Link href={`/collector/${encodeURIComponent(collector.id)}/config?q=create_accounts`}>Change in Config</Link>
        </Alert>
      )}
      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}

      {batchRunning && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          <b>Batch {batch?.action === "disable" ? "disable" : "enable"} in progress</b> — {batch?.processed ?? 0}
          {batch?.total ? ` / ${batch.total}` : ""} processed ({batch?.ok ?? 0} ok{batch?.failed ? `, ${batch.failed} failed` : ""}).
          {" "}Enabling/disabling accounts is locked until it finishes; operaciones@ will get an email with the result.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Account number</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Customer id</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Alias</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <Tooltip title={`What the collector sees as "Descripción" in the vendor portal`}><span>Description</span></Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>This collector has no accounts yet. Add one or upload a file.</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>No {status === "all" ? "" : status + " "}accounts match{q ? ` “${q}”` : ""}.</TableCell></TableRow>
            ) : filtered.map((a) => (
              <TableRow key={a.accountNumber} hover sx={a.deleted ? { "& td:not(:last-child)": { opacity: 0.55 } } : undefined}>
                <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                  {a.accountNumber}
                  <Tooltip title="Copy"><IconButton size="small" sx={{ ml: 0.5 }} onClick={() => { navigator.clipboard?.writeText(a.accountNumber); notify("Copied"); }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{a.customerId}</TableCell>
                <TableCell>{a.alias}</TableCell>
                <TableCell>{a.email || <span style={{ color: "#888" }}>—</span>}</TableCell>
                <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.description || undefined}>
                  {a.description || <span style={{ color: "#888" }}>—</span>}
                </TableCell>
                <TableCell>{a.deleted ? <StatusChip kind="bad" label="Disabled" /> : <StatusChip kind="ok" label="Active" />}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Edit customer id, email, alias"><IconButton size="small" onClick={() => setEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  {a.deleted
                    ? <Tooltip title={batchRunning ? "A batch is running" : "Enable account"}><span><IconButton size="small" sx={{ color: "#166534" }} disabled={batchRunning} onClick={() => setToggle(a)}><CheckIcon fontSize="small" /></IconButton></span></Tooltip>
                    : <Tooltip title={batchRunning ? "A batch is running" : "Disable account"}><span><IconButton size="small" color="error" disabled={batchRunning} onClick={() => setToggle(a)}><PowerSettingsNewIcon fontSize="small" /></IconButton></span></Tooltip>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">{filtered.length} shown · {rows.length} loaded{nextPage ? " (more available)" : ""}</Typography>
          <Box sx={{ flex: 1 }} />
          {nextPage && <Button size="small" onClick={loadMore} disabled={more} startIcon={more ? <CircularProgress size={12} /> : undefined}>Load more</Button>}
          <Tooltip title="Refresh"><span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
        </Stack>
      </TableContainer>

      <AddAccountDialog open={addOpen} ctx={ctx} onClose={() => setAddOpen(false)}
        onDone={async () => { setAddOpen(false); notify("Account created"); await load(); }} />
      <EditAccountDialog account={edit} ctx={ctx} onClose={() => setEdit(null)}
        onDone={async () => { setEdit(null); notify("Account updated"); await load(); }} />
      <UploadDialog open={upOpen} ctx={ctx} onClose={() => setUpOpen(false)}
        onDone={async () => { setUpOpen(false); notify("File queued — track it in Upload history"); await loadTasks(); }} />

      <ConfirmDialog open={!!toggle} onClose={() => setToggle(null)} onConfirm={doToggle} danger={!toggle?.deleted}
        title={toggle?.deleted ? "Enable account" : "Disable account"} okLabel={toggle?.deleted ? "Enable" : "Disable"}>
        {toggle?.deleted
          ? <><code>{toggle.accountNumber}</code>{toggle.alias ? ` (${toggle.alias})` : ""} will accept transfers again.</>
          : <>Disabling <code>{toggle?.accountNumber}</code>{toggle?.alias ? ` (${toggle.alias})` : ""} <b>rejects incoming transfers</b> to this account at the bank. It can be enabled again later.</>}
      </ConfirmDialog>

      <ConfirmDialog open={bulk === "disable"} onClose={() => setBulk(null)} onConfirm={doBulk} danger typeToConfirm={collector.id}
        title="Disable all accounts" okLabel="Start disabling" busyLabel="Starting…">
        <Alert severity="error" sx={{ mb: 1.5 }}><b>Every active account of this collector will reject incoming transfers.</b> Payers will see the transfer bounce at their bank.</Alert>
        Runs in the background over <b>every</b> account (not just the loaded ones), one real bank call each — it can take a while. We remember exactly which accounts this disables, so you can re-enable precisely those later. operaciones@ gets an email when it finishes.
      </ConfirmDialog>
      <ConfirmDialog open={bulk === "enable"} onClose={() => setBulk(null)} onConfirm={doBulk} title="Enable all accounts" okLabel="Start enabling" busyLabel="Starting…">
        Re-enables the <b>{batch?.disabled_count ?? 0}</b> account(s) that were disabled by admin via this panel (not those disabled by the account owner). Runs in the background; operaciones@ gets an email when it finishes.
      </ConfirmDialog>

      <Drawer anchor="right" open={histOpen} onClose={() => setHistOpen(false)} PaperProps={{ sx: { width: { xs: "100vw", md: 760 } } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Upload history</Typography>
          <Typography variant="body2" color="text.secondary">{collector.id}</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh"><IconButton size="small" onClick={loadTasks}><RefreshIcon /></IconButton></Tooltip>
          <IconButton size="small" onClick={() => setHistOpen(false)}><CloseIcon /></IconButton>
        </Stack>
        <Box sx={{ p: 3 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Created</TableCell><TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell><TableCell sx={{ fontWeight: 700 }}>Result</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks === null ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                ) : tasks.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>No uploads for this collector.</TableCell></TableRow>
                ) : [...tasks].sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "")).map((t) => (
                  <TableRow key={`${t.pk}|${t.sk}`} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(t.createdAt)}
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>updated {fmtDateTime(t.updatedAt)}</Typography></TableCell>
                    <TableCell><code style={{ fontSize: 12 }}>{t.taskType}</code>
                      {t.downloadFile && <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>{t.downloadFile}</Typography>}</TableCell>
                    <TableCell><StatusChip kind={TASK_OK_RE.test(t.status) ? "ok" : /ERR|FAIL/i.test(t.status) ? "bad" : "info"} label={t.status} /></TableCell>
                    <TableCell>{t.statusDescription || "—"}</TableCell>
                    <TableCell align="right">
                      {/* CP-457: la descarga es el CSV de RESULTADO (CoreDB /Admin/async_tasks/download), que solo existe cuando la tarea terminó bien. */}
                      {TASK_OK_RE.test(t.status) && <Tooltip title={t.resultFile ? `Download result (${t.resultFile})` : "Download result"}><IconButton size="small" onClick={() => downloadUploadFile(t).catch((e) => notify(String(e?.message ?? e), "error"))}><DownloadIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>Processing runs asynchronously — refresh to see progress.</Typography>
        </Box>
      </Drawer>
    </>
  );
}

// ----- CP-444: preview/validación de la carga masiva antes de subir -----------
//
// El formato real NO es "customer_id,email" (eso era de un doc de diseño v2, nunca
// implementado) — se confirmó contra 151 uploads reales en `async_tasks` (prod):
// el worker real (un Lambda aparte, "AsyncTasks.Function", fuera de este repo) espera
// columnas `id;alias1;email` (alias2/alias3/cvu/aliasAsignado opcionales), separadas
// por PUNTO Y COMA — no coma (estándar de Excel en es-AR). Nombres de columna distintos
// ("Customer Id", "ALIAS 1", "Mail", etc.) son la causa real más común de fallas
// ("No members are mapped for type 'AsyncTasks.Function+Cuenta'"), junto con alias
// duplicados ("BindAlias ... El Alias de CBU/CVU ya se encuentra en uso"). El id
// repetido en cambio el propio backend lo tolera (lo saltea y sigue con el resto).

/** Parser CSV con delimitador fijo. OJO: NO trata '"' como comilla de escape —
 *  a propósito, porque el backend real (AsyncTasks.Function, Function.cs:169)
 *  parsea con `Quote = '\0'` (comillas deshabilitadas). Un campo entre comillas
 *  que tenga el delimitador adentro se corta igual ahí, tanto acá como en el
 *  backend real — si tratáramos '"' como escape acá, el preview aprobaría
 *  archivos que el backend real rechaza (BadDataFound, visto en logs reales). */
function parseCsv(text: string, delim: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  const noBom = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const s = noBom.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === delim) { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => !(r.length === 1 && r[0].trim() === ""));
}

// El backend real (AsyncTasks.Function) no auto-detecta separador — usa ";" fijo.
// Un archivo separado por "," se lee entero como una sola columna y falla sin
// ningún aviso claro (confirmado en logs reales de prod: header "id,alias1,email"
// leído como ColumnCount=1), así que acá se exige ";" en vez de tolerar los dos.
const REQUIRED_DELIM = ";";

const ACCENT_RE = /[ÁÉÍÓÚáéíóúÑñ]/;
const ONLY_DIGITS_RE = /^[0-9]+$/;

export interface UploadPreview {
  totalRows:         number;
  willCreate:        number;
  errors:            { line: number; id: string; reason: string }[];
  duplicateIdCount:  number;   // informativo: repetido DENTRO del archivo, no bloquea
  existingIdCount:   number;   // ya existe como cuenta — subir el archivo le CAMBIA alias/email (ver UploadDialog)
  /** CP-484: "full" = carga masiva normal (+ descripciones si hay columna);
   *  "descOnly" = archivo `id;descripcion` sin alias: no va al worker, solo actualiza descripciones. */
  mode:              "full" | "descOnly";
  /** CP-484: descripciones a aplicar después (celda vacía = no se toca, no está acá). */
  descriptions:      { line: number; id: string; description: string }[];
  /** CP-361: alias que hoy usa OTRA cuenta del mismo collector. El worker lo reasigna a la
   *  nueva y la cuenta anterior queda sin alias. Pablo (14/10): dentro del mismo collector
   *  es deseable, con aviso en la UI. No bloquea. */
  reassigned:        { line: number; id: string; alias: string; fromId: string; fromAccount: string }[];
}

/** Valida formato y que el alias no se repita dentro del archivo — es lo que realmente
 *  rompe altas en producción. Un alias que ya usa otra cuenta del collector no bloquea:
 *  se reasigna y se avisa (CP-361).
 *  El id repetido DENTRO del archivo es solo informativo (el backend lo tolera).
 *  Pero un id que ya existe como cuenta es otra cosa: confirmado contra el código real
 *  de AsyncTasks.Function (Modificacion de Cuenta, ~línea 220) que el worker compara
 *  alias/email actuales contra los del CSV y, si difieren, los CAMBIA de verdad en el
 *  banco (SuperCambiarAlias/SuperModificarCuenta) — no los saltea. Por eso se cuenta
 *  aparte para poder avisar fuerte en la UI. El email no se valida ni se chequea por
 *  duplicados (puede repetirse sin problema). */
function validateAccountsCsv(text: string, existing: Account[]): UploadPreview | { formatError: string } {
  const existingAliases = new Set(existing.map((a) => a.alias.trim().toLowerCase()).filter(Boolean));
  const existingIds = new Set(existing.map((a) => a.customerId.trim().toLowerCase()).filter(Boolean));
  // CP-484: el alias actual de la MISMA cuenta no es "ya en uso por otra" (re-subir una cuenta
  // existente con su propio alias no le cambia nada en el worker).
  const aliasById = new Map(existing.map((a) => [a.customerId.trim().toLowerCase(), a.alias.trim().toLowerCase()] as const));
  const ownerByAlias = new Map(existing.filter((a) => a.alias.trim())
    .map((a) => [a.alias.trim().toLowerCase(), a] as const));
  const firstLine = text.split(/\r\n|\r|\n/, 1)[0] ?? "";
  if (!firstLine.includes(REQUIRED_DELIM)) {
    return { formatError: `The file must use ";" to separate columns — found no ";" in the header ("${firstLine.slice(0, 80)}"). Re-save it with ";" as separator, not ",".` };
  }
  if (text.includes('"')) {
    return { formatError: `The file can't contain quote characters (") — the real uploader doesn't support them and a field with "${REQUIRED_DELIM}" inside quotes gets cut into extra columns. Remove all quotes and try again.` };
  }
  const table = parseCsv(text, REQUIRED_DELIM);
  if (table.length === 0) return { formatError: "The file is empty." };
  const rawHeader = table[0].map((h) => h.trim());
  // CP-444: antes se chequeaban mayúsculas primero — si el header venía mal escrito
  // (no solo mal caseado), el mensaje sugería "pasalo a minúsculas" con el nombre
  // incorrecto tal cual, y recién en un segundo intento aparecía el error real
  // ("falta la columna id"). Comparar contra los nombres esperados en minúsculas
  // primero, para que el problema de fondo (falta/está mal el header) se vea antes
  // que el de formato (mayúsculas).
  const header = rawHeader.map((h) => h.toLowerCase());
  const idIdx     = header.indexOf("id");
  const alias1Idx = header.indexOf("alias1");
  const descIdx   = header.findIndex(isDescriptionHeader);   // CP-484
  // CP-484: `id;descripcion` sin alias = solo actualizar descripciones de cuentas existentes.
  if (idIdx !== -1 && alias1Idx === -1 && descIdx !== -1) {
    const upper = rawHeader.filter((h) => h !== h.toLowerCase());
    if (upper.length > 0) {
      return { formatError: `Column headers must be lowercase: ${upper.map((h) => `"${h}" → "${h.toLowerCase()}"`).join(", ")}.` };
    }
    return validateDescriptionsOnly(table, idIdx, descIdx, existingIds);
  }
  if (idIdx === -1 || alias1Idx === -1) {
    return { formatError: `Missing required column "${idIdx === -1 ? "id" : "alias1"}" in the header (found: ${header.join(", ") || "none"}).` };
  }
  const upperCols = rawHeader.filter((h) => h !== h.toLowerCase());
  if (upperCols.length > 0) {
    return { formatError: `Column headers must be lowercase: ${upperCols.map((h) => `"${h}" → "${h.toLowerCase()}"`).join(", ")}.` };
  }
  const aliasIdxs = [alias1Idx, header.indexOf("alias2"), header.indexOf("alias3")].filter((i) => i >= 0);

  const seenAliases = new Set<string>();
  const seenIds = new Set<string>();
  const errors: UploadPreview["errors"] = [];
  const descriptions: UploadPreview["descriptions"] = [];
  const reassigned: UploadPreview["reassigned"] = [];
  let duplicateIdCount = 0;
  let existingIdCount = 0;
  let willCreate = 0;

  for (let i = 1; i < table.length; i++) {
    const r = table[i];
    const line = i + 1;   // 1-based, contando el header
    const id = (r[idIdx] ?? "").trim();
    const aliases = aliasIdxs.map((idx) => (r[idx] ?? "").trim()).filter(Boolean);
    if (!id) { errors.push({ line, id, reason: "Empty id" }); continue; }
    if (ACCENT_RE.test(id)) { errors.push({ line, id, reason: `ID "${id}" has accents or ñ — not allowed` }); continue; }
    if (!aliases.length) { errors.push({ line, id, reason: "Empty alias1" }); continue; }

    // CP-444: alias1/alias2/alias3 NO son 3 alias a asignar juntos — confirmado contra
    // AsyncTasks.Function (SuperCrearAlias/CrearAliasBIND, ~líneas 250-274 y 666-690):
    // el worker prueba alias1, y solo si falla intenta alias2, y solo si ESE falla
    // intenta alias3 — se asigna el PRIMERO que funciona, no los tres. Antes acá se
    // fallaba la fila entera si CUALQUIERA de los alias cargados ya existía, aunque
    // el siguiente de la lista estuviera libre.
    let chosenAlias: string | null = null;
    const rejected: string[] = [];
    for (const alias of aliases) {
      // Coelsa/Bind rechazan el alta si el alias tiene acentos o ñ, o es solo numérico —
      // mismo regex que "Edit alias" (ALIAS_RE), para no validar distinto entre carga
      // única y masiva.
      if (!ALIAS_RE.test(alias)) { rejected.push(`"${alias}" (invalid format)`); continue; }
      if (ONLY_DIGITS_RE.test(alias.replace(/[.-]/g, ""))) { rejected.push(`"${alias}" (only numbers)`); continue; }
      const a = alias.toLowerCase();
      if (seenAliases.has(a)) { rejected.push(`"${alias}" (duplicate within this file)`); continue; }
      if (existingAliases.has(a) && aliasById.get(id.toLowerCase()) !== a) {
        // CP-361: otra cuenta del mismo collector lo tiene → se reasigna, con aviso.
        const owner = ownerByAlias.get(a);
        reassigned.push({ line, id, alias, fromId: owner?.customerId ?? "?", fromAccount: owner?.accountNumber ?? "?" });
      }
      chosenAlias = alias;
      break;
    }
    if (!chosenAlias) {
      errors.push({ line, id, reason: aliases.length > 1 ? `None of the provided aliases can be used: ${rejected.join(", ")}` : rejected[0] });
      continue;
    }
    seenAliases.add(chosenAlias.toLowerCase());

    if (seenIds.has(id.toLowerCase())) duplicateIdCount++;
    seenIds.add(id.toLowerCase());
    if (existingIds.has(id.toLowerCase())) existingIdCount++;
    willCreate++;

    // CP-484: descripción opcional (vacía = no se toca).
    if (descIdx !== -1) {
      const rawDesc = r[descIdx] ?? "";
      const dErr = descriptionError(rawDesc);
      if (dErr) { errors.push({ line, id, reason: dErr }); willCreate--; continue; }
      const d = normalizeDescription(rawDesc);
      if (d) descriptions.push({ line, id, description: d });
    }
  }
  return { totalRows: table.length - 1, willCreate, errors, duplicateIdCount, existingIdCount, mode: "full", descriptions, reassigned };
}

/** CP-484: archivo `id;descripcion` (sin alias). Cada id tiene que ser una cuenta existente. */
function validateDescriptionsOnly(table: string[][], idIdx: number, descIdx: number, existingIds: Set<string>): UploadPreview {
  const errors: UploadPreview["errors"] = [];
  const descriptions: UploadPreview["descriptions"] = [];
  const seenIds = new Set<string>();
  let duplicateIdCount = 0;
  for (let i = 1; i < table.length; i++) {
    const r = table[i];
    const line = i + 1;
    const id = (r[idIdx] ?? "").trim();
    if (!id) { errors.push({ line, id, reason: "Empty id" }); continue; }
    if (!existingIds.has(id.toLowerCase())) { errors.push({ line, id, reason: "No account with this id in the collector" }); continue; }
    const rawDesc = r[descIdx] ?? "";
    const dErr = descriptionError(rawDesc);
    if (dErr) { errors.push({ line, id, reason: dErr }); continue; }
    if (seenIds.has(id.toLowerCase())) duplicateIdCount++;
    seenIds.add(id.toLowerCase());
    const d = normalizeDescription(rawDesc);
    if (d) descriptions.push({ line, id, description: d });
  }
  return { totalRows: table.length - 1, willCreate: 0, errors, duplicateIdCount, existingIdCount: descriptions.length, mode: "descOnly", descriptions, reassigned: [] };
}

/** `File.text()` siempre decodifica UTF-8 — los CSV reales suelen venir en Windows-1252
 *  (Excel es-AR), lo que corrompe tildes/ñ ("AHUMARÍN" → "AHUMAR�N", visto en logs reales
 *  de prod). Probamos UTF-8 primero; si aparece el carácter de reemplazo, recodificamos los
 *  mismos bytes como windows-1252. No altera el archivo real que se sube — solo cómo lo leemos acá. */
function decodeCsvBytes(buf: ArrayBuffer): string {
  const utf8 = new TextDecoder("utf-8", { fatal: false }).decode(buf);
  if (!utf8.includes("�")) return utf8;
  try { return new TextDecoder("windows-1252").decode(buf); } catch { return utf8; }
}

function exportCsv(id: string, rows: Account[]) {
  const esc = (s: string) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const csv = ["account_number,customer_id,alias,email,description,status", ...rows.map((a) => [a.accountNumber, a.customerId, a.alias, a.email ?? "", a.description, a.deleted ? "disabled" : "active"].map(esc).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const el = document.createElement("a"); el.href = url; el.download = `${id}-accounts.csv`; el.click();
  URL.revokeObjectURL(url);
}

function AddAccountDialog({ open, ctx, onClose, onDone }: { open: boolean; ctx: CollectorCtx; onClose: () => void; onDone: () => Promise<void> }) {
  const [cid, setCid]     = useState("");
  const [alias, setAlias] = useState("");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState<string | null>(null);
  useEffect(() => { if (open) { setCid(""); setAlias(""); setErr(null); } }, [open]);
  // Mismas reglas que la carga masiva (validateUpload): no vacío y sin acentos/ñ.
  // Los espacios SÍ van: CoreDB no los valida y muchos collectors usan el nombre
  // del pagador como id ("DURET OSCAR LUIS").
  const hasAccent = ACCENT_RE.test(cid);
  const ok = cid.trim() !== "" && !hasAccent;
  // CP-255: alias opcional — si se deja vacío, CoreDB sigue asignando uno solo.
  const aliasOk = alias.trim() === "" || ALIAS_RE.test(alias.trim());
  const isMx = ctx.collector.paymentsProcessor.startsWith("MX");

  async function save() {
    setBusy(true); setErr(null);
    try {
      const accountNumber = await addAccount(ctx.collector.id, cid.trim());
      if (alias.trim() !== "") {
        // La cuenta ya se creó en CoreDB en este punto — si falla el alias (ej. "ya en uso"),
        // igual hay que refrescar la lista para no dar a entender que no pasó nada.
        try { await editAccountAlias(ctx.collector.id, accountNumber, alias.trim()); }
        catch (e: any) { await onDone(); throw new Error(`Account ${accountNumber} was created, but setting the alias failed: ${String(e?.message ?? e)}`); }
      }
      await onDone();
    }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add account<Typography variant="body2" color="text.secondary">{ctx.collector.id} · {ctx.collector.paymentsProcessor} will issue a new {isMx ? "CLABE" : "CVU"}</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Customer id" value={cid} autoFocus disabled={busy} placeholder="e.g. 2346" onChange={(e) => setCid(e.target.value)}
            error={cid !== "" && !ok} sx={squareInput}
            helperText={hasAccent ? "No accents or ñ." : "The collector's own id for the payer. Must be unique within the collector."} />
          <TextField label="Alias" value={alias} disabled={busy} placeholder="Leave empty to let the bank assign one" onChange={(e) => setAlias(e.target.value)}
            error={alias !== "" && !aliasOk} sx={squareInput}
            helperText="Optional. 6–20 chars, letters, digits, dot or dash — same rules as Edit account." />
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !ok || !aliasOk} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>{busy ? "Creating…" : "Create account"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function EditAccountDialog({ account, ctx, onClose, onDone }: { account: Account | null; ctx: CollectorCtx; onClose: () => void; onDone: () => Promise<void> }) {
  const [cid, setCid]     = useState("");
  const [email, setEmail] = useState("");
  const [alias, setAlias] = useState("");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState<string | null>(null);
  const [confirmAlias, setConfirmAlias] = useState(false);
  useEffect(() => { if (account) { setCid(account.customerId); setEmail(account.email ?? ""); setAlias(account.alias); setErr(null); } }, [account]);
  if (!account) return null;

  const aliasChanged = alias.trim() !== account.alias;
  const dataChanged  = cid.trim() !== account.customerId || email.trim() !== (account.email ?? "");
  const emailOk = email.trim() === "" || EMAIL_RE.test(email.trim());
  const aliasOk = ALIAS_RE.test(alias.trim());
  const valid = emailOk && aliasOk && cid.trim() !== "" && (aliasChanged || dataChanged);

  async function save() {
    setBusy(true); setErr(null);
    try {
      if (dataChanged)  await editAccountCustomerAndEmail(ctx.collector.id, account!.accountNumber, cid.trim(), email.trim());
      if (aliasChanged) await editAccountAlias(ctx.collector.id, account!.accountNumber, alias.trim());
      setConfirmAlias(false);
      await onDone();
    } catch (e: any) { setErr(String(e?.message ?? e)); setConfirmAlias(false); }
    finally { setBusy(false); }
  }
  return (
    <>
      <Dialog open={!!account && !confirmAlias} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>Edit account<Typography variant="body2" color="text.secondary">{ctx.collector.id} · <code>{account.accountNumber}</code></Typography></DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Customer id" value={cid} disabled={busy} onChange={(e) => setCid(e.target.value)} sx={squareInput} />
            <TextField label="Email" type="email" value={email} disabled={busy} placeholder="payer@example.com" onChange={(e) => setEmail(e.target.value)}
              error={!emailOk} helperText={emailOk ? " " : "Invalid email"} sx={squareInput} />
            <TextField label="Alias" value={alias} disabled={busy} onChange={(e) => setAlias(e.target.value)} error={!aliasOk}
              helperText="6–20 chars, letters, digits, dot or dash. Changing it asks for confirmation — it breaks saved transfers." sx={squareInput} />
            {err && <Alert severity="error">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={busy || !valid} onClick={() => (aliasChanged ? setConfirmAlias(true) : save())}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>{busy ? "Saving…" : "Save"}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={confirmAlias} onClose={() => setConfirmAlias(false)} onConfirm={save} title="Change alias?" okLabel="Change alias" busyLabel="Saving…">
        Payers using <b>{account.alias}</b> will no longer be able to transfer to it. The new alias <b>{alias.trim()}</b> takes effect immediately at the bank.
      </ConfirmDialog>
    </>
  );
}

// CP-484: resultado por fila del paso de descripciones.
interface DescResult { line: number; id: string; description: string; account: string | null; status: "pending" | "ok" | "skipped" | "error"; detail: string }

function DescriptionResults({ rows }: { rows: DescResult[] }) {
  const n = (st: DescResult["status"]) => rows.filter((r) => r.status === st).length;
  const done = rows.length - n("pending");
  return (
    <>
      <Alert severity={n("error") ? "error" : n("skipped") ? "warning" : done === rows.length ? "success" : "info"}>
        Descriptions: {done}/{rows.length} processed · {n("ok")} updated{n("skipped") ? ` · ${n("skipped")} skipped` : ""}{n("error") ? ` · ${n("error")} with errors` : ""}
      </Alert>
      {(n("skipped") > 0 || n("error") > 0) && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
          <Table size="small" stickyHeader>
            <TableHead><TableRow><TableCell>Line</TableCell><TableCell>id</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
            <TableBody>
              {rows.filter((r) => r.status === "skipped" || r.status === "error").map((r) => (
                <TableRow key={r.line}>
                  <TableCell>{r.line}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.id}</TableCell>
                  <TableCell>{r.detail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

function UploadDialog({ open, ctx, onClose, onDone }: { open: boolean; ctx: CollectorCtx; onClose: () => void; onDone: () => Promise<void> }) {
  const [file, setFile]       = useState<File | null>(null);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<UploadPreview | { formatError: string } | null>(null);
  const [descResults, setDescResults] = useState<DescResult[] | null>(null);   // CP-484
  const [phase, setPhase]     = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) { setFile(null); setErr(null); setPreview(null); setDescResults(null); setPhase(null); } }, [open]);

  // CP-444: antes de subir, validamos el CSV en el browser (formato de columnas, separador,
  // alias vacío/duplicado) contra las cuentas ya existentes del collector — el alias repetido
  // es lo que realmente falla en producción ("El Alias de CBU/CVU ya se encuentra en uso").
  // Solo se admite CSV: XLSX no se puede parsear sin agregar una librería nueva, y no hay
  // forma de validarlo antes de subir, así que directamente se rechaza.
  async function pickFile(f: File | null) {
    setFile(f); setErr(null); setPreview(null); setDescResults(null);
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) { setPreview({ formatError: `"${f.name}" is not a CSV file. Only .csv is accepted.` }); return; }
    setChecking(true);
    try {
      const [buf, existing] = await Promise.all([f.arrayBuffer(), listAllAccounts(ctx.collector.id)]);
      const text = decodeCsvBytes(buf);
      setPreview(validateAccountsCsv(text, existing));
    } catch (e: any) { setErr(`Could not check the file: ${String(e?.message ?? e)}`); }
    finally { setChecking(false); }
  }

  const blockingErrorCount = preview ? ("formatError" in preview ? 1 : preview.errors.length) : 0;
  const canUpload = !!file && file.size <= 5 * 1024 * 1024 && !checking && !busy && blockingErrorCount === 0 && !descResults;
  const okPreview = preview && !("formatError" in preview) ? preview : null;

  // CP-484: paso 2 — aplicar descripciones cuenta por cuenta (web_account). Las cuentas
  // nuevas se resuelven cuando termina la tarea del worker. Si no hay columna
  // descripcion (o está vacía en todas las filas), este paso no corre.
  async function applyDescriptions(rowsToApply: UploadPreview["descriptions"], waitForTaskSince: number | null) {
    const results: DescResult[] = rowsToApply.map((d): DescResult => ({ ...d, account: null, status: "pending", detail: "" }));
    const show = () => setDescResults(results.map((r) => ({ ...r })));
    show();
    const byId = async () => new Map((await listAllAccounts(ctx.collector.id)).map((a) => [a.customerId.trim().toLowerCase(), a] as const));
    let accounts = await byId();
    const missing = () => results.some((r) => r.status === "pending" && !accounts.has(r.id.toLowerCase()));

    if (waitForTaskSince != null && missing()) {
      setPhase("Waiting for the bulk upload to finish to get the new accounts…");
      const deadline = Date.now() + 10 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 5_000));
        const task = pickUploadTask(await listUploadTasks(ctx.collector.id), waitForTaskSince);
        if (task && isTaskFinished(task.status)) break;
      }
      accounts = await byId();
    }

    setPhase("Updating descriptions…");
    for (const r of results) {
      const acc = accounts.get(r.id.toLowerCase());
      if (!acc) { r.status = "skipped"; r.detail = "No account with this id (not created by the upload?)"; show(); continue; }
      r.account = acc.accountNumber;
      // web_account fuerza on_received="accept": en una cuenta deshabilitada la reactivaría.
      if (acc.deleted || acc.onReceived === "reject") { r.status = "skipped"; r.detail = "Account is disabled — not touched"; show(); continue; }
      try { await setAccountDescription(ctx.collector.id, acc.accountNumber, r.description); r.status = "ok"; }
      catch (e: any) { r.status = "error"; r.detail = String(e?.message ?? e); }
      show();
    }
    setPhase(null);
  }

  async function send() {
    if (!file || !okPreview) return;
    setBusy(true); setErr(null);
    try {
      let since: number | null = null;
      if (okPreview.mode === "full") {
        since = Date.now();
        await uploadAccountsFile(ctx.collector.id, file);
      }
      if (okPreview.descriptions.length > 0) await applyDescriptions(okPreview.descriptions, since);
      await onDone();
      if (okPreview.descriptions.length === 0) onClose();
    }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); setPhase(null); }
  }
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Upload accounts file<Typography variant="body2" color="text.secondary">{ctx.collector.id} · bulk creation, processed in the background</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {okPreview?.mode !== "descOnly" && <Alert severity="warning">
            If a row's <code>id</code> already has an account, this does <b>not</b> skip it — it updates that account's
            alias and email to match the file, changing what's live at the bank. Double-check the file before uploading
            if you're re-running a previous upload.
          </Alert>}
          <Box onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void pickFile(f); }}
            sx={{ border: "2px dashed", borderColor: file ? BRAND.primary : "divider", borderRadius: 2, p: 3.5, textAlign: "center", cursor: "pointer", color: "text.secondary" }}>
            <UploadIcon sx={{ color: BRAND.primary }} />
            {file ? <Typography><b>{file.name}</b> · {(file.size / 1024).toFixed(0)} KB</Typography>
              : <><Typography><b style={{ color: BRAND.primary }}>Choose a file</b> or drag it here</Typography><Typography variant="caption">CSV only · max 5 MB</Typography></>}
            <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => void pickFile(e.target.files?.[0] ?? null)} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Columns <code>id;alias1;email</code> (<code>alias2</code>/<code>alias3</code> optional), separated by <code>;</code> — headers must be lowercase.
            An alias can never repeat — within the file or against an existing account.
            Optional <code>descripcion</code> column (what the collector sees as "Descripción" in the vendor portal, up to 200 characters, one line):
            it's applied after the upload, account by account — keep this window open until it finishes. An empty cell leaves the current description untouched.
            A file with only <code>id;descripcion</code> just updates the descriptions of existing accounts.
          </Typography>

          {checking && <Alert severity="info" icon={<CircularProgress size={16} />}>Checking the file against the current accounts…</Alert>}

          {preview && "formatError" in preview && (
            <Alert severity="error">{preview.formatError} Fix the file and choose it again.</Alert>
          )}

          {preview && !("formatError" in preview) && (
            <>
              <Alert severity={preview.errors.length ? "error" : preview.existingIdCount > 0 || preview.reassigned.length > 0 ? "warning" : "success"}>
                {preview.totalRows} row{preview.totalRows === 1 ? "" : "s"} · {preview.willCreate} will be processed
                {preview.errors.length > 0 && ` · ${preview.errors.length} with errors — fix and choose the file again`}
                {preview.duplicateIdCount > 0 && ` · ${preview.duplicateIdCount} id repeated within the file (only needed once — the repeat may error)`}
                {preview.mode === "full" && preview.existingIdCount > 0 && ` · ${preview.existingIdCount} id${preview.existingIdCount === 1 ? "" : "s"} already have an account — their alias/email will be UPDATED, not created`}
                {preview.mode === "full" && preview.descriptions.length > 0 && ` · ${preview.descriptions.length} description${preview.descriptions.length === 1 ? "" : "s"} will be set after the upload`}
                {preview.reassigned.length > 0 && ` · ${preview.reassigned.length} alias${preview.reassigned.length === 1 ? "" : "es"} will be MOVED from another account of this collector (see below)`}
                {preview.mode === "descOnly" && ` · descriptions only: ${preview.descriptions.length} account${preview.descriptions.length === 1 ? "" : "s"} will be updated (alias/email are not touched)`}
              </Alert>
              {preview.reassigned.length > 0 && (
                <Alert severity="warning" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
                  <b>Alias reassignment.</b> These aliases are already used by another account of this collector.
                  The upload will move each one to the new account, and the previous account will be left
                  <b> without alias</b>.
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead><TableRow><TableCell>Line</TableCell><TableCell>Alias</TableCell><TableCell>Moves from</TableCell><TableCell>To id</TableCell></TableRow></TableHead>
                    <TableBody>
                      {preview.reassigned.slice(0, 50).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.line}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{r.alias}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{r.fromId} · {r.fromAccount}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{r.id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {preview.reassigned.length > 50 && <Typography variant="caption">…and {preview.reassigned.length - 50} more.</Typography>}
                </Alert>
              )}
              {preview.errors.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Line</TableCell><TableCell>id</TableCell><TableCell>Problem</TableCell></TableRow></TableHead>
                    <TableBody>
                      {preview.errors.slice(0, 50).map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.line}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{e.id || "—"}</TableCell>
                          <TableCell>{e.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {preview.errors.length > 50 && (
                <Typography variant="caption" color="text.secondary">…and {preview.errors.length - 50} more.</Typography>
              )}
            </>
          )}

          {phase && <Alert severity="info" icon={<CircularProgress size={16} />}>{phase} Keep this window open.</Alert>}
          {descResults && <DescriptionResults rows={descResults} />}

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{descResults && !busy ? "Close" : "Cancel"}</Button>
        <Button variant="contained" onClick={send} disabled={!canUpload} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}>
          {busy ? (phase ? "Working…" : "Uploading…") : okPreview?.mode === "descOnly" ? "Update descriptions" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
